/**
 * finishSignIn — the one place that defines "what it means to be signed in".
 *
 * Every entry point that produces a session (password login, SSO callback,
 * invite acceptance, MFA challenge, MFA enrolment) crosses this seam instead of
 * re-implementing the post-auth sequence. It persists the session, hydrates
 * permissions/identity/views into Redux + localStorage, prefetches the
 * reference data permissioned users need, and returns where to send the user.
 *
 * Pass the auth response when you have one (it stores the tokens); omit it when
 * the session was already stored (e.g. the two-step MFA enrolment). Returns the
 * in-app path to navigate to — or the two-factor enrolment page (carrying that
 * path as ?next=) when the tenant requires MFA and this user has not set it up.
 */
import { store } from '../redux_store/store';
import { storeSession } from './session';
import { getPermissions } from '../helpers/auth';
import { getCurrentUser } from '../api_service/user_api';
import { storeIdentity } from './identity';
import { checkMfaGate, clearMfaGate, mfaEnrollPath } from './mfaGate';
import { setPermissions } from '../redux_store/features/auth_permissions_reducer';
import { fetchJobTitles } from '../redux_store/features/job_title_reducer';
import { fetchSkillLevels } from '../redux_store/features/level_reducer';
import { fetchSkills } from '../redux_store/features/skill_reducer';
import { fetchSubscriptions } from '../redux_store/features/subscription_reducer';
import { fetchSubSkills } from '../redux_store/features/subskill_reducer';
import {
  setAccessibleViews,
  setCurrentView,
} from '../redux_store/features/ViewReducer';

export type SignInResponse = {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
};

// A same-site path only: one leading slash, no protocol-relative form, and no
// backslash (browsers treat "\" as "/" in http(s) URLs).
const isInAppPath = (p?: string): p is string =>
  !!p && /^\/(?![/\\])/.test(p) && !p.includes('\\') && p !== '/';

export async function finishSignIn(
  res?: SignInResponse | null,
  opts?: { deepLink?: string },
): Promise<string> {
  if (res?.accessToken) storeSession(res);

  const token = localStorage.getItem('token');
  const perms = token ? getPermissions(token) : [];
  store.dispatch(setPermissions(perms));

  try {
    const user = await getCurrentUser();
    if (user?.id) {
      localStorage.setItem('id', user.id);
      localStorage.setItem('firstname', user.firstName || '');
      localStorage.setItem('lastname', user.lastName || '');
      const pic = user.profilePic;
      if (pic && pic !== 'null' && pic !== 'undefined') {
        localStorage.setItem('profilePic', pic);
      } else {
        localStorage.removeItem('profilePic');
      }
      localStorage.setItem('jobStatus', user.jobSeekingStatus || '');
      if (user.email) localStorage.setItem('email', user.email);
      if (user.role) localStorage.setItem('role', user.role);
      if (user.view) {
        store.dispatch(setCurrentView(user.view[0]));
        store.dispatch(setAccessibleViews(user.view));
      }
    }
  } catch {
    /* non-fatal: routing below still works from the token's permissions */
  }

  if (perms.length > 0) {
    store.dispatch(fetchSkills());
    store.dispatch(fetchSkillLevels());
    store.dispatch(fetchJobTitles());
    store.dispatch(fetchSubSkills());
    store.dispatch(fetchSubscriptions());
  }

  const target = isInAppPath(opts?.deepLink)
    ? opts!.deepLink!
    : perms.length === 0 ? '/assigned-assessments' : '/home';

  // Tenant-enforced MFA: employees must enrol before they reach the app.
  // Candidates are exempt by policy (and have no permissions), so the gate is
  // explicitly cleared for them rather than left over from a previous user.
  clearMfaGate();
  if (perms.length > 0 && (await checkMfaGate())) return mfaEnrollPath(target);
  return target;
}
